import { profile_data } from 'src/stores/account/user';
import type { Selection } from 'src/types/Device';
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
import { delete_profile, save_profile } from '../profile_helpers';
import { resetSelection } from './settings_config_helpers';
import { convertSeconds, formatTimeLabel, formatTwoDigits } from '../timer_helpers';

const getButtonModeOptions = () => ({
	follow: {
		type: 'switch',
		text: get(format)('btn_mode_follow_description', {
			default:
				'Set Shelly device to be "Toggle" switch. Act as a flip switch with one state for "ON" and one state for "OFF".',
		}),
	},
	momentary: {
		type: 'button',
		text: get(format)('btn_mode_momentary_description', {
			default: 'Set Shelly device to be "Momentary" switch. Push for "ON" or Push for "OFF".',
		}),
	},
	cycle: {
		type: 'button',
		text: get(format)('btn_mode_cycle_description', {
			default:
				'Set Shelly device to be "Cycle" switch. At the push of a input, the sequence is: Ch1-ON Ch2-OFF, Ch1-OFF Ch2-ON, Ch1 & Ch2 ON, Ch1 & Ch2 OFF',
		}),
	},
	flip: {
		type: 'switch',
		text: get(format)('btn_mode_flip_description', {
			default: 'Set Shelly device to be "Edge" switch. Changes state on every hit.',
		}),
	},
	detached: {
		type: 'any',
		text: get(format)('btn_mode_detached_description', {
			default:
				'Set Shelly device to be in "Detached" switch mode - switch is separated from the relays.',
		}),
	},
	activate: {
		type: 'any',
		text: get(format)('btn_mode_activate_description', {
			default:
				'Activation switch - Used with motion sensor. Any input turns ON and resets Auto Off timer',
		}),
	},
});

const getInitialStateOptions = () => ({
	on: {
		type: 'any',
		text: get(format)('init_state_on_description', {
			default: 'Configure Shelly device to Turn ON, when it has power.',
		}),
	},
	off: {
		type: 'any',
		text: get(format)('init_state_off_description', {
			default: 'Configure Shelly device to Turn OFF, when it has power.',
		}),
	},
	restore_last: {
		type: 'any',
		text: get(format)('init_state_report_last_description', {
			default: 'Configure Shelly device to Restore the last mode it was in, when it has power.',
		}),
	},
	match_input: {
		type: 'switch',
		text: get(format)('init_state_match_input_description', {
			default:
				'Operates the Shelly device according to the state of the switch or button connected to the device.',
		}),
	},
});

// Optimized template state management
export const startEditingTemplate = (template) => ({
	id: template.id,
	name: template.name,
	notes: template.notes || '',
});

export const cancelEditingTemplate = () => ({
	id: null,
	name: '',
	notes: '',
});

// Optimized save template edit with better error handling
export const saveTemplateEdit = async (
	editingTemplateId,
	editingTemplateName,
	editingTemplateNotes,
	error,
) => {
	// Early validation
	if (!editingTemplateId || !editingTemplateName.trim()) {
		return {
			newError: error,
			id: editingTemplateId,
			name: editingTemplateName,
			notes: editingTemplateNotes,
		};
	}

	try {
		const currentTemplates = (get(profile_data).fav_configs ?? {}) as Record<string, any>;
		const template = currentTemplates[editingTemplateId];

		if (!template) {
			return {
				newError: get(format)('text_template_error_template_not_found', {
					default: 'Template not found.',
				}),
				id: editingTemplateId,
				name: editingTemplateName,
				notes: editingTemplateNotes,
			};
		}

		const updatedTemplates = {
			...currentTemplates,
			[editingTemplateId]: {
				...template,
				name: editingTemplateName.trim(),
				notes: editingTemplateNotes.trim() || undefined,
			},
		};

		const response = await save_profile('fav_configs', updatedTemplates);

		if (response?.isok) {
			profile_data.set({ ...get(profile_data), fav_configs: updatedTemplates });
			return {
				newError: null,
				id: null,
				name: '',
				notes: '',
			};
		}

		// Handle save failure
		const errorMessage = get(format)('text_template_error_rename_failed', {
			default: 'Failed to rename template. Please try again.',
		});
		return {
			newError: errorMessage,
			id: editingTemplateId,
			name: editingTemplateName,
			notes: editingTemplateNotes,
		};
	} catch (err) {
		console.error('Error renaming template:', err);
		const errorMessage = get(format)('text_template_error_rename_failed', {
			default: 'Failed to rename template. Please try again.',
		});
		return {
			newError: errorMessage,
			id: editingTemplateId,
			name: editingTemplateName,
			notes: editingTemplateNotes,
		};
	}
};

export const handleDeleteTemplate = async (templateId: string, selected: Selection, error: string) => {
	try {
		const currentTemplates = (get(profile_data).fav_configs ?? {}) as Record<string, any>;
		if (!(templateId in currentTemplates)) return;

		const { [templateId]: _, ...updatedTemplates } = (get(profile_data).fav_configs ?? {}) as Record<
			string,
			any
		>;

		if (Object.keys(updatedTemplates).length === 0) {
			await delete_profile('fav_configs');
		} else {
			await save_profile('fav_configs', updatedTemplates);
		}

		if (selected.id === templateId) {
			resetSelection(selected);
		}
	} catch (err) {
		error = get(format)('text_template_error_delete_template', {
			default: 'Failed to delete template. Please try again.',
		});
		throw new Error(error);
	}
};

/**
 * Formats a template value for display based on its key type
 * @param key - The setting key (e.g., 'auto_on_delay', 'auto_on', 'in_mode')
 * @param value - The value to format
 * @returns Formatted string representation of the value
 */
export const formatTemplateValue = (key: string, value: any): string => {
	switch (key) {
		case 'auto_on_delay':
		case 'auto_off_delay':
			if (typeof value === 'number') {
				const { hours, minutes, seconds } = convertSeconds(value);
				return formatTimeLabel(
					formatTwoDigits(hours),
					formatTwoDigits(minutes),
					formatTwoDigits(seconds),
					'00',
				);
			}
			break;

		case 'auto_on':
		case 'auto_off':
			if (typeof value === 'boolean') {
				return value
					? get(format)('xt1_template_enabled', { default: 'Enabled' })
					: get(format)('xt1_template_disabled', { default: 'Disabled' });
			}
			break;

		case 'in_mode':
			const buttonModeOptions = getButtonModeOptions();
			const buttonMode = buttonModeOptions[value];
			if (buttonMode) {
				return buttonMode.text;
			}
			break;

		case 'initial_state':
			const initialStateOptions = getInitialStateOptions();
			const initialState = initialStateOptions[value];
			if (initialState) {
				return initialState.text;
			}
			break;
	}

	return String(value);
};
